// ErrorBoundary - top-level catch-all that swaps a render crash for a reload/
// dismiss fallback so one broken page doesn't white-screen the whole SPA.

import { AlertOctagon, RotateCcw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-dvh flex items-center justify-center px-4">
        <div className="card p-8 max-w-md w-full text-center">
          <AlertOctagon className="w-6 h-6 text-verdict-malicious mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-ink-100">Something went wrong</h1>
          <p className="text-sm text-ink-400 mt-1.5">
            The page hit an unexpected error. Reloading usually fixes it; if not, contact support.
          </p>
          <pre className="mt-4 text-[11px] text-ink-500 font-mono bg-ink-900 border border-ink-800 rounded p-2 text-left overflow-auto max-h-32">
            {this.state.error.message}
          </pre>
          <div className="mt-5 flex items-center justify-center gap-2">
            <button onClick={() => location.reload()} className="btn-primary">
              <RotateCcw className="w-4 h-4" />
              Reload
            </button>
            <button onClick={this.reset} className="btn-secondary">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }
}
